import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type { ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { allowanceLabel, formatMinor, planValueNote, type AllowanceUnit } from "@talenthop/shared";
import { api } from "../../lib/api.js";
import { Alert, Card, CheckBig, Info, Sparkles } from "../../components/icons.js";
import { useEntitlements } from "./useEntitlements.js";
import {
  ORG_PLANS_KEY,
  serverMessage,
  useOrgPlans,
  type OrgPlan,
  type OrgPlansResponse,
  type QuoteResponse,
} from "./planUsage.js";

/**
 * Users · Plans & usage (0094). Everything the customer sees about what they've bought and what's
 * left, in three units only — jobs, resumes, AI interviews — pooled across the organisation. The
 * catalog is the owner's (admin Plans); nothing on this page is a hardcoded plan or price. Every
 * number in the checkout comes from the server quote (shared quotePlan), which is exactly what
 * the gateway is asked to charge.
 */

// ---- Razorpay Checkout (loaded on demand — same idiom as VerifySignup.tsx) ----
interface RzpResponse {
  razorpay_payment_id: string;
  razorpay_order_id: string;
  razorpay_signature: string;
}
interface RzpCtor {
  new (o: {
    key: string;
    order_id: string;
    amount: number;
    currency: string;
    name?: string;
    description?: string;
    handler: (r: RzpResponse) => void;
    modal?: { ondismiss?: () => void };
    theme?: { color?: string };
  }): { open: () => void };
}
declare global {
  interface Window {
    Razorpay?: RzpCtor;
  }
}
function loadRazorpay(): Promise<boolean> {
  if (window.Razorpay) return Promise.resolve(true);
  return new Promise((resolve) => {
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

const UNITS: { unit: AllowanceUnit; label: string }[] = [
  { unit: "job", label: "Jobs" },
  { unit: "scan", label: "Resumes" },
  { unit: "interview", label: "AI interviews" },
];

function includes(p: { jobs: number; resumes: number; interviews: number }): string {
  return [
    p.jobs > 0 ? allowanceLabel("job", p.jobs) : null,
    p.resumes > 0 ? allowanceLabel("scan", p.resumes) : null,
    p.interviews > 0 ? allowanceLabel("interview", p.interviews) : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

export function Plans(): ReactNode {
  const q = useOrgPlans(30_000);
  const { entitlements } = useEntitlements();
  const [buying, setBuying] = useState<OrgPlan | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const d = q.data;
  // Arrived from New requirement's "no job credits" state — once a job is available, offer the way back.
  const [params] = useSearchParams();
  const fromNew = params.get("next") === "new";

  const header = (
    <div className="page-h">
      <div>
        <h1>Plans &amp; usage</h1>
        <div className="sub">What your organisation has left, and plans to top up — jobs, resumes and AI interviews, pooled for everyone</div>
      </div>
    </div>
  );

  if (q.isLoading) {
    return (
      <section className="t1s-page t1s-plans">
        {header}
        <p className="mut">Loading your plan…</p>
      </section>
    );
  }
  if (!d) {
    return (
      <section className="t1s-page t1s-plans">
        {header}
        <div className="banner crit"><Alert /><span>Couldn't load your plan. Refresh to try again.</span></div>
      </section>
    );
  }
  const money = (minor: number) => formatMinor(minor, d.currency);
  // No "subscription" exists — plans are bought in any quantity into one pool — so the honest
  // highlight is the plan last bought (purchases arrive newest first, paid only).
  const lastBought = d.purchases[0]?.planName ?? null;

  return (
    <section className="t1s-page t1s-plans">
      {header}
      {entitlements.freeTier && (() => {
        // Numbers are the page's own pooled usage — the same read as the meters below.
        const left = [
          allowanceLabel("interview", d.usage.interview.remaining),
          allowanceLabel("scan", d.usage.scan.remaining),
          d.jobsMetered ? allowanceLabel("job", d.usage.job.remaining) : null,
        ].filter(Boolean).join(", ");
        const ends = entitlements.freePlanEndsAt
          ? new Date(entitlements.freePlanEndsAt).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })
          : null;
        return (
          <div className="t1s-freeplan card" role="status">
            <Sparkles />
            <div>
              <div className="t">
                You&rsquo;re on the free plan — {left} left{ends ? ` · ends ${ends}` : ""}
              </div>
              <div className="d">Every paid plan adds adaptive follow-up questions and resume-tailored questions.</div>
            </div>
          </div>
        );
      })()}
      {done && (
        <div className="banner ok">
          <CheckBig />
          <span>{done}</span>
        </div>
      )}
      {fromNew && d && (!d.jobsMetered || d.usage.job.remaining > 0) && (
        <div className="banner info">
          <CheckBig />
          <span style={{ flex: 1 }}>You have a job available — you can create your requirement now.</span>
          <Link to="/app/new" className="btn primary sm">New requirement</Link>
        </div>
      )}

      <div className="t1s-usage" aria-label="What's left">
        {UNITS.map(({ unit, label }) => {
          const u = d.usage[unit];
          const unmetered = unit === "job" && !d.jobsMetered;
          // The bar shows what is LEFT, matching the big number (audit 20: direction was ambiguous).
          const leftPct = u.total > 0 ? Math.max(0, Math.min(100, Math.round((u.remaining / u.total) * 100))) : 0;
          const low = u.total > 0 && leftPct < 10;
          return (
            <div key={unit} className="card">
              <div className="l">{label}</div>
              {unmetered ? (
                <>
                  <div className="n tnum">—</div>
                  <div className="d" style={{ marginTop: 12 }}>Not limited on your account</div>
                </>
              ) : (
                <>
                  <div className="n tnum">
                    {u.remaining.toLocaleString("en-IN")} <small>left</small>
                  </div>
                  <div
                    className={`t1s-meter${low ? " low" : ""}`}
                    role="meter"
                    aria-label={`${label} left`}
                    aria-valuemin={0}
                    aria-valuemax={u.total}
                    aria-valuenow={u.remaining}
                  >
                    <i style={{ width: `${leftPct}%` }} />
                  </div>
                  <div className="d">
                    {leftPct}% left · {u.used.toLocaleString("en-IN")} used of {u.total.toLocaleString("en-IN")}
                    {u.nextExpiry ? ` · next expiry ${new Date(u.nextExpiry).toLocaleDateString()}` : ""}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>

      {d.legacyBalance > 0 && (
        <div className="banner t1s-neutral">
          <Info />
          <span>
            Your organisation also has a prepaid balance from before plans
            (<b className="tnum">{d.legacyBalance.toLocaleString("en-IN")} units</b>). It's used automatically for
            resumes and AI interviews once your plan allowance runs out.
          </span>
        </div>
      )}

      <div>
        <div className="t1s-sechead">
          <h2>Plans</h2>
          <span className="mut">
            Buy any quantity — allowances add to your organisation's pool
            {d.tax.pct > 0 ? ` · prices ${d.tax.inclusive ? "include" : "exclude"} ${d.tax.pct}% ${d.tax.name}` : ""}
          </span>
        </div>
        {d.plans.length === 0 ? (
          <div className="card pad mut sm">No plans are available in {d.currency} right now.</div>
        ) : (
          <div className="t1s-plan-grid">
            {d.plans.map((p) => {
              const current = !p.isCustom && lastBought != null && p.name === lastBought;
              return (
                <article
                  key={p.id}
                  className={`t1s-plan${p.isFeatured ? " featured" : ""}${current ? " current" : ""}`}
                  aria-label={`${p.name} plan`}
                >
                  <div className="t1s-plan-h">
                    <h3>{p.name}</h3>
                    {current && <span className="pill t">Last purchased</span>}
                    {p.isFeatured && <span className="pill v">Recommended</span>}
                  </div>
                  {p.description && <div className="t1s-plan-desc">{p.description}</div>}
                  {p.isCustom ? (
                    <>
                      <div className="t1s-price"><b>Custom</b></div>
                      <ul className="t1s-inc"><li><CheckBig /> Custom volumes and terms</li></ul>
                      <a className="btn" href="mailto:sales@talenthopai.com?subject=TalentHop%20Enterprise">
                        Contact us
                      </a>
                    </>
                  ) : (
                    <>
                      <PriceBlock plan={p} data={d} />
                      <ul className="t1s-inc">
                        {p.jobs > 0 && <li><CheckBig /> {allowanceLabel("job", p.jobs)}</li>}
                        {p.resumes > 0 && <li><CheckBig /> {allowanceLabel("scan", p.resumes)}</li>}
                        {p.interviews > 0 && <li><CheckBig /> {allowanceLabel("interview", p.interviews)}</li>}
                        <li><CheckBig /> Adaptive follow-ups &amp; resume-tailored questions</li>
                        <li className="per">Per unit bought · valid {p.validityMonths} months</li>
                      </ul>
                      <button
                        type="button"
                        className="btn primary"
                        disabled={!d.canBuy}
                        data-tip={d.canBuy ? undefined : "Only organisation admins can buy plans"}
                        data-tip-pos="top"
                        onClick={() => {
                          setDone(null);
                          setBuying(p);
                        }}
                      >
                        <Card /> Buy {p.name}
                      </button>
                    </>
                  )}
                </article>
              );
            })}
          </div>
        )}
        {!d.canBuy && (
          <div className="t1s-plan-note" style={{ marginTop: 12 }}>
            Only organisation admins can buy plans — ask yours when you're running low.
          </div>
        )}
      </div>

      {d.purchases.length > 0 && (
        <div className="card t1s-purchases">
          <div className="card-h"><h3>Purchases</h3></div>
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Plan</th>
                  <th className="r">Discount</th>
                  <th className="r">{d.tax.name || "Tax"}</th>
                  <th className="r">Paid</th>
                </tr>
              </thead>
              <tbody>
                {d.purchases.map((p) => (
                  <tr key={p.id}>
                    <td className="sm tnum">{new Date(p.at).toLocaleDateString()}</td>
                    <td>{p.planName} × {p.quantity}</td>
                    <td className="tnum r">{p.discountMinor > 0 ? `−${money(p.discountMinor)}` : "—"}</td>
                    <td className="tnum r">{money(p.taxMinor)}</td>
                    <td className="tnum r"><b>{money(p.totalMinor)}</b></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {buying && (
        <CheckoutModal
          plan={buying}
          data={d}
          onClose={() => setBuying(null)}
          onPaid={(msg) => {
            setBuying(null);
            setDone(msg);
          }}
        />
      )}
    </section>
  );
}

/** Price, the struck-out "was" price, tax basis, per-interview estimate and the you-save note —
 *  every figure from the server catalog + the shared planValueNote, nothing hard-coded. The
 *  saving states its assumption (audit 20: an unsourced claim in tiny green text). */
function PriceBlock({ plan, data }: { plan: OrgPlan; data: OrgPlansResponse }): ReactNode {
  const money = (minor: number) => formatMinor(minor, data.currency);
  const note = planValueNote(plan.priceMinor, plan.interviews, data.humanInterviewMinor);
  const showWas = plan.compareAtMinor != null && plan.compareAtMinor > plan.priceMinor;
  return (
    <>
      <div className="t1s-price">
        <b className="tnum">{money(plan.priceMinor)}</b>
        {showWas && (
          <s className="tnum" aria-label={`was ${money(plan.compareAtMinor!)}`}>{money(plan.compareAtMinor!)}</s>
        )}
        {data.tax.pct > 0 && (
          <span className="tax">{data.tax.inclusive ? "incl." : "+"} {data.tax.pct}% {data.tax.name}</span>
        )}
      </div>
      {note && <div className="t1s-per tnum">≈ {money(note.perInterviewMinor)} per AI interview</div>}
      {note?.savingMinor != null && data.humanInterviewMinor != null && (
        <div className="t1s-save">
          <b>Save ~{money(note.savingMinor)}</b> vs human interviews
          <br />
          <span>assumes {money(data.humanInterviewMinor)} per human-run interview</span>
        </div>
      )}
    </>
  );
}

function CheckoutModal({
  plan,
  data,
  onClose,
  onPaid,
}: {
  plan: OrgPlan;
  data: OrgPlansResponse;
  onClose: () => void;
  onPaid: (msg: string) => void;
}): ReactNode {
  const qc = useQueryClient();
  const [qty, setQty] = useState(plan.minQty);
  const [couponDraft, setCouponDraft] = useState("");
  const [coupon, setCoupon] = useState<string | undefined>(undefined);
  const [quote, setQuote] = useState<QuoteResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const money = (minor: number) => formatMinor(minor, data.currency);
  const qtyOk = Number.isInteger(qty) && qty >= plan.minQty && qty <= plan.maxQty;

  // Live server quote — re-priced on every quantity / coupon change (debounced).
  useEffect(() => {
    if (!qtyOk) return;
    let alive = true;
    const t = setTimeout(() => {
      api
        .post<QuoteResponse>("/org/plans/quote", { planId: plan.id, quantity: qty, couponCode: coupon })
        .then((r) => {
          if (alive) {
            setQuote(r);
            setErr(null);
          }
        })
        .catch((e: unknown) => alive && setErr(serverMessage(e, "Couldn't price this — try again.")));
    }, 250);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [plan.id, qty, coupon, qtyOk]);

  const refresh = () => void qc.invalidateQueries({ queryKey: ORG_PLANS_KEY });
  const paidMsg = `Payment received — ${plan.name} × ${qty} added to your organisation.`;

  async function pay(): Promise<void> {
    setErr(null);
    setBusy(true);
    try {
      const start = await api.post<{
        purchaseId: string;
        paid?: boolean;
        orderId?: string;
        keyId?: string;
        currency?: string;
        amountMinor?: number;
      }>("/org/plans/purchases", { planId: plan.id, quantity: qty, couponCode: coupon });
      if (start.paid) {
        refresh();
        onPaid(paidMsg);
        return;
      }
      const ok = await loadRazorpay();
      if (!ok || !window.Razorpay) {
        setErr("Couldn't load the payment window. Check your connection and try again.");
        setBusy(false);
        return;
      }
      new window.Razorpay({
        key: start.keyId!,
        order_id: start.orderId!,
        amount: start.amountMinor!,
        currency: start.currency!,
        name: "TalentHop",
        description: `${plan.name} × ${qty}`,
        theme: { color: "#0B2419" }, // Razorpay needs a literal colour — CSS variables are ignored
        modal: { ondismiss: () => setBusy(false) },
        handler: (r) => {
          void api
            .post(`/org/plans/purchases/${start.purchaseId}/confirm`, {
              razorpayOrderId: r.razorpay_order_id,
              razorpayPaymentId: r.razorpay_payment_id,
              razorpaySignature: r.razorpay_signature,
            })
            .then(() => {
              refresh();
              onPaid(paidMsg);
            })
            .catch((e: unknown) => {
              setBusy(false);
              refresh();
              setErr(serverMessage(e, "The payment went through but confirming it failed — refresh in a minute; it settles automatically."));
            });
        },
      }).open();
    } catch (e) {
      setBusy(false);
      setErr(serverMessage(e, "Couldn't start the purchase. Please try again."));
    }
  }

  const qv = quote?.quote;
  return (
    <div className="rq-modal-scrim" role="dialog" aria-modal="true" onClick={busy ? undefined : onClose}>
      <div className="rq-modal t1s-checkout" onClick={(e) => e.stopPropagation()}>
        <div className="rq-modal-h">
          <div className="t">Buy {plan.name}</div>
          <div className="d">{includes(plan)} per unit · valid {plan.validityMonths} months</div>
        </div>
        <div className="rq-modal-b">
          <div className="rq-line">
            <label className="k" htmlFor="plan-qty">Quantity</label>
            <span className="v t1s-qty" style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
              <button type="button" className="rq-btn ghost" aria-label="Fewer" data-tip="Fewer"
                disabled={busy || qty <= plan.minQty} onClick={() => setQty((n) => Math.max(plan.minQty, n - 1))}>−</button>
              {/* Read-only on purpose: quantity moves only with the − / + buttons (no typing, no spinner). */}
              <input
                id="plan-qty"
                className="input tnum t1s-qty-val"
                type="text"
                readOnly
                tabIndex={-1}
                aria-live="polite"
                value={Number.isFinite(qty) ? qty : ""}
                disabled={busy}
              />
              <button type="button" className="rq-btn ghost" aria-label="More" data-tip="More"
                disabled={busy || qty >= plan.maxQty} onClick={() => setQty((n) => Math.min(plan.maxQty, n + 1))}>+</button>
            </span>
          </div>
          {!qtyOk && <div className="mut sm">Choose between {plan.minQty} and {plan.maxQty}.</div>}

          <div className="rq-line">
            <label className="k" htmlFor="plan-coupon">Coupon</label>
            <span className="v t1s-coupon">
              <input
                id="plan-coupon"
                className="input"
                value={couponDraft}
                disabled={busy}
                placeholder="Enter coupon code"
                autoComplete="off"
                onChange={(e) => setCouponDraft(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && setCoupon(couponDraft.trim() || undefined)}
              />
              <button type="button" className="rq-btn ghost sm" disabled={busy || !couponDraft.trim()}
                onClick={() => setCoupon(couponDraft.trim() || undefined)}>Apply</button>
            </span>
          </div>
          {quote?.couponError && <div className="mut sm" style={{ color: "var(--crit)" }}>{quote.couponError}</div>}

          {qv && qtyOk && (
            <>
              <div className="rq-line">
                <span className="k">You get</span>
                <span className="v sm">{includes({
                  jobs: qv.allowances.jobs, resumes: qv.allowances.resumes, interviews: qv.allowances.interviews,
                })}</span>
              </div>
              <div className="rq-line">
                <span className="k">{money(qv.unitPriceMinor)} × {qv.quantity}</span>
                <span className="v tnum">{money(qv.subtotalMinor)}</span>
              </div>
              {qv.discountMinor > 0 && (
                <div className="rq-line">
                  <span className="k">Coupon {quote?.couponApplied}</span>
                  <span className="v tnum">−{money(qv.discountMinor)}</span>
                </div>
              )}
              {qv.taxPct > 0 && (
                <div className="rq-line">
                  <span className="k">
                    {qv.taxInclusive ? `Includes ${data.tax.name} ${qv.taxPct}%` : `${data.tax.name} ${qv.taxPct}%`}
                  </span>
                  <span className="v tnum">{money(qv.taxMinor)}</span>
                </div>
              )}
              <div className="rq-line strong">
                <span className="k">Total{qv.taxInclusive && qv.taxPct > 0 ? ` (incl. ${data.tax.name})` : ""}</span>
                <span className="v tnum">{money(qv.totalMinor)}</span>
              </div>
            </>
          )}

          {err && (
            <div className="banner crit" style={{ marginTop: 12 }}>
              <Alert />
              <span>{err}</span>
            </div>
          )}
        </div>
        <div className="rq-modal-f">
          <button type="button" className="rq-btn ghost" disabled={busy} onClick={onClose}>Cancel</button>
          <button type="button" className="rq-btn primary t1s-pay" disabled={busy || !qv || !qtyOk} onClick={() => void pay()}>
            <Card />
            {busy ? (
              <span>Processing…</span>
            ) : qv ? (
              // "Pay ₹ 7,500": the symbol and the amount as separate pieces with an even gap.
              (() => {
                const [, sym = "", amount = money(qv.totalMinor)] = /^([^\d\s-]*)\s*(.*)$/.exec(money(qv.totalMinor)) ?? [];
                return (
                  <>
                    <span>Pay</span>
                    {sym && <span>{sym}</span>}
                    <span className="tnum">{amount}</span>
                  </>
                );
              })()
            ) : (
              <span>Pay</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
